// src/salesAssessment/SalesAssessmentReportContent.js
import { Fragment } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  WorkOutline as WorkIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Glosario de roles de venta: los 8 roles que calcula el backend, agrupados por tipo de venta.
// Los nombres en es/en permiten destacar el rol principal y el secundario sea cual sea el idioma del test.
const SALES_ROLE_GROUPS = [
  { key: 'transactional', color: '#2e7d32', roles: ['distributor', 'provider', 'revealer'] },
  { key: 'opportunity', color: '#1565c0', roles: ['creator', 'pioneer', 'innovator'] },
  { key: 'consultative', color: '#e65100', roles: ['consultant', 'partner'] },
];

const SALES_ROLE_NAMES = {
  distributor: ['distribuidor', 'distributor'],
  provider: ['proveedor', 'provider'],
  revealer: ['revelador', 'revealer'],
  creator: ['creador', 'creator'],
  pioneer: ['pionero', 'pioneer'],
  innovator: ['innovador', 'innovator'],
  consultant: ['consultor', 'consultant'],
  partner: ['partner'],
};

const normalizeRoleName = (name) =>
  String(name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

export default function SalesAssessmentReportContent({ data }) {
  const { t } = useTranslation();

  if (!data) {
    return (
      <Alert severity="warning">
        {t('salesAssessment.report.noDataAvailable')}
      </Alert>
    );
  }

  const analysis = data.aiAnalysis;
  if (!analysis) {
    return (
      <Alert severity="warning">
        {t('salesAssessment.report.analysisNotAvailable')}
      </Alert>
    );
  }

  const competenciaScores = analysis.competencias?.map(c => c.puntaje || 0) || [];
  const maxScore = competenciaScores.length ? Math.max(...competenciaScores) : 1;
  const minScore = competenciaScores.length ? Math.min(...competenciaScores) : 0;
  const scoreRange = maxScore - minScore || 1;

  const getScoreColor = (score) => {
    const third = scoreRange / 3;
    if (score >= minScore + 2 * third) return '#4caf50';
    if (score >= minScore + third) return '#ff9800';
    return '#f44336';
  };

  // Glosario: destaca el rol principal y el secundario del test
  const mainRole = normalizeRoleName(analysis.rolComercial?.principal);
  const secondaryRole = normalizeRoleName(analysis.rolComercial?.secundario);
  const getRoleHighlight = (roleKey) => {
    const names = SALES_ROLE_NAMES[roleKey] || [];
    if (mainRole && names.includes(mainRole)) return 'main';
    if (secondaryRole && names.includes(secondaryRole)) return 'secondary';
    return null;
  };

  return (
    <Box>
      {/* Header con información del postulante */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: '#f8fafc' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <PersonIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          </Grid>
          <Grid item xs>
            <Typography variant="h5" gutterBottom>
              {data.applicantName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data.applicantEmail}
            </Typography>
            {data.positionData?.name && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  icon={<WorkIcon />}
                  label={data.positionData.name}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
          </Grid>
          <Grid item>
            <Chip
              label={t('salesAssessment.report.assessmentCompleted')}
              color="success"
              icon={<CheckIcon />}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 1. Resumen Ejecutivo */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('salesAssessment.report.executiveSummary')}
          sx={{ bgcolor: '#e3f2fd' }}
          avatar={<TrendingUpIcon color="primary" />}
        />
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('salesAssessment.report.effectivenessLevel')}
            </Typography>
            <Typography variant="body2" paragraph>
              {analysis.resumenEjecutivo?.nivelEfectividad}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('salesAssessment.report.sellerType')}
            </Typography>
            <Typography variant="body2" paragraph>
              {analysis.resumenEjecutivo?.tipoVendedor}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('salesAssessment.report.optimalContexts')}
            </Typography>
            <Typography variant="body2">
              {analysis.resumenEjecutivo?.contextosOptimos}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* 2. Perfil Comercial Actual */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('salesAssessment.report.currentSalesProfile')}
          sx={{ bgcolor: '#f3e5f5' }}
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('salesAssessment.report.salesProcess')}
              </Typography>
              <Typography variant="body2" paragraph>
                {analysis.perfilComercial?.procesoVentas}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('salesAssessment.report.customerInteraction')}
              </Typography>
              <Typography variant="body2" paragraph>
                {analysis.perfilComercial?.interaccionClientes}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('salesAssessment.report.decisionMaking')}
              </Typography>
              <Typography variant="body2" paragraph>
                {analysis.perfilComercial?.tomaDecisiones}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('salesAssessment.report.closingProgress')}
              </Typography>
              <Typography variant="body2">
                {analysis.perfilComercial?.avanceCierre}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 3. Competencias Comerciales */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('salesAssessment.report.competenciesEvaluation')}
          sx={{ bgcolor: '#fff3e0' }}
        />
        <CardContent>
          {analysis.competencias?.map((comp, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {comp.nombre}
                </Typography>
                <Chip
                  label={comp.puntaje}
                  size="small"
                  sx={{
                    bgcolor: getScoreColor(comp.puntaje),
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {comp.interpretacion}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                💡 {comp.implicacionDiaria}
              </Typography>
              {index < analysis.competencias.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* 4. Rol Comercial */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('salesAssessment.report.predominantRole')}
          sx={{ bgcolor: '#e8f5e9' }}
        />
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('salesAssessment.report.mainRole')}
            </Typography>
            <Chip
              label={analysis.rolComercial?.principal}
              color="primary"
              size="large"
              sx={{ fontWeight: 600, fontSize: '1rem', mb: 2 }}
            />
            {analysis.rolComercial?.secundario && (
              <Chip
                label={`${t('salesAssessment.report.secondary')} ${analysis.rolComercial.secundario}`}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Box>

          <Typography variant="body2" paragraph>
            {analysis.rolComercial?.explicacionPrincipal}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('salesAssessment.report.optimalSaleType')}
          </Typography>
          <Typography variant="body2">
            {analysis.rolComercial?.tipoVentaOptima}
          </Typography>
        </CardContent>
      </Card>

      {/* 4b. Glosario de roles de venta */}
      {analysis.rolComercial && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={t('salesAssessment.report.rolesGlossary.title')}
            sx={{ bgcolor: '#e8f5e9' }}
          />
          <CardContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('salesAssessment.report.rolesGlossary.intro')}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '24%' }}>
                    {t('salesAssessment.report.rolesGlossary.roleColumn')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('salesAssessment.report.rolesGlossary.definitionColumn')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {SALES_ROLE_GROUPS.map((group) => (
                  <Fragment key={group.key}>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 700, color: group.color, borderBottom: 'none', pt: 2 }}>
                        {t(`salesAssessment.report.rolesGlossary.groups.${group.key}`)}
                      </TableCell>
                    </TableRow>
                    {group.roles.map((roleKey) => {
                      const highlight = getRoleHighlight(roleKey);
                      return (
                        <TableRow
                          key={roleKey}
                          sx={{ bgcolor: highlight === 'main' ? 'action.selected' : highlight === 'secondary' ? 'action.hover' : 'transparent' }}
                        >
                          <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                            {t(`salesAssessment.report.rolesGlossary.roles.${roleKey}.name`)}
                            {highlight && (
                              <Box sx={{ mt: 0.5 }}>
                                <Chip
                                  label={t(highlight === 'main'
                                    ? 'salesAssessment.report.rolesGlossary.mainTag'
                                    : 'salesAssessment.report.rolesGlossary.secondaryTag')}
                                  color="primary"
                                  variant={highlight === 'main' ? 'filled' : 'outlined'}
                                  size="small"
                                />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="body2">
                              {t(`salesAssessment.report.rolesGlossary.roles.${roleKey}.definition`)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* 5. Ajuste al Rol e Industria */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('salesAssessment.report.roleIndustryFit')}
          sx={{ bgcolor: '#fce4ec' }}
        />
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('salesAssessment.report.fitLevel')}
            </Typography>
            <Chip
              label={analysis.ajusteRolIndustria?.nivel}
              color={analysis.ajusteRolIndustria?.nivel === 'Alto' ? 'success' : 'warning'}
              size="medium"
            />
          </Box>
          <Typography variant="body2">
            {analysis.ajusteRolIndustria?.explicacion}
          </Typography>
        </CardContent>
      </Card>

      {/* 6. Aspectos a Gestionar */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('salesAssessment.report.aspectsToManage')}
          sx={{ bgcolor: '#fff9c4' }}
          avatar={<WarningIcon color="warning" />}
        />
        <CardContent>
          <List>
            {analysis.aspectosGestionar?.map((aspecto, index) => (
              <ListItem key={index} alignItems="flex-start" sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {aspecto.observacion}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        <strong>{t('salesAssessment.report.importance')}</strong> {aspecto.importancia}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t('salesAssessment.report.context')}</strong> {aspecto.contextoRelevancia}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* 7. Prioridades de Desarrollo */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('salesAssessment.report.developmentPriorities')}
          sx={{ bgcolor: '#e1f5fe' }}
        />
        <CardContent>
          {analysis.prioridadesDesarrollo?.map((prioridad, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={`${t('salesAssessment.report.priority')} ${index + 1}`}
                  size="small"
                  color="primary"
                />
                <Typography variant="subtitle1" fontWeight={600}>
                  {prioridad.prioridad}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>{t('salesAssessment.report.impact')}</strong> {prioridad.impactoResultados}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {prioridad.explicacion}
              </Typography>
              {index < analysis.prioridadesDesarrollo.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* 8. Plan de Acción */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('salesAssessment.report.actionPlan')}
          sx={{ bgcolor: '#f1f8e9' }}
          avatar={<TimelineIcon color="success" />}
        />
        <CardContent>
          <Grid container spacing={3}>
            {/* 30 días */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" color="primary" gutterBottom>
                {t('salesAssessment.report.days30')}
              </Typography>
              <List dense>
                {analysis.planAccion?.dias30?.map((accion, index) => (
                  <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
                    <CheckIcon sx={{ mr: 1, mt: 0.5, fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="body2">{accion}</Typography>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* 60 días */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" color="primary" gutterBottom>
                {t('salesAssessment.report.days60')}
              </Typography>
              <List dense>
                {analysis.planAccion?.dias60?.map((accion, index) => (
                  <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
                    <CheckIcon sx={{ mr: 1, mt: 0.5, fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="body2">{accion}</Typography>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* 90 días */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" color="primary" gutterBottom>
                {t('salesAssessment.report.days90')}
              </Typography>
              <List dense>
                {analysis.planAccion?.dias90?.map((accion, index) => (
                  <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
                    <CheckIcon sx={{ mr: 1, mt: 0.5, fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="body2">{accion}</Typography>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 9. Recomendaciones Finales */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('salesAssessment.report.finalRecommendations')}
          sx={{ bgcolor: '#ede7f6' }}
        />
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('salesAssessment.report.synthesis')}
          </Typography>
          <Typography variant="body2" paragraph>
            {analysis.recomendacionesFinales?.sintesis}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('salesAssessment.report.practicalAdvice')}
          </Typography>
          <Typography variant="body2" paragraph>
            {analysis.recomendacionesFinales?.consejosPracticos}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={600} color="primary">
              {analysis.recomendacionesFinales?.cierre}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}